import { createApi } from '@reduxjs/toolkit/query/react'
import type { ThreadDeal } from '@adthlete/shared-frontend'
import { appBaseUrl } from '@adthlete/shared-frontend'
import { baseQuery } from './baseQuery'
import type { Campaign } from '@/types'

// Types from collaborationsSlice
export interface Submission {
  id: string
  version: number
  contentDescription: string | null
  /** Inline steps: a list of links. Video reviews: { deliverables: { [id]: { video, caption } } } */
  contentUrls: { type: string; url: string; name?: string }[] | Record<string, any> | null
  notes: string | null
  status: 'pending_review' | 'approved' | 'revision_requested' | 'superseded'
  submittedAt: string
  reviewedAt: string | null
  reviewNotes: Record<string, any> | null
  submitter: {
    id: string
    name: string
    avatar: string | null
  } | null
  metadata?: Record<string, unknown>
}

export interface PlatformSubmissionRequirement {
  platform: string
  submission_type: 'code' | 'attestation'
  status: 'pending' | 'completed'
  submitted_value?: string | boolean
  submitted_at?: string
}

export interface MilestoneCheckIn {
  active: boolean
  response: 'on_track' | 'needs_more_time' | null
  responseAt: string | null
}

export interface MilestoneDateProposal {
  id: string
  proposedDueDate: string
  reason: string | null
  status: 'open' | 'accepted' | 'declined' | 'cancelled' | 'expired'
  responseNote: string | null
  createdAt: string
  respondedAt: string | null
  expiresAt: string
}

export interface Milestone {
  id: string
  name: string
  slug?: string
  description: string | null
  sequenceOrder: number
  dueDate: string | null
  status: 'pending' | 'in_progress' | 'submitted' | 'revision_requested' | 'approved' | 'completed'
  requiresApproval: boolean
  completedAt: string | null
  metadata?: {
    step?: string
    platforms?: PlatformSubmissionRequirement[]
  } | null
  submissions: Submission[]
  checkIn?: MilestoneCheckIn | null
  dateProposal?: MilestoneDateProposal | null
}

/** A review note on a submission (services/submissionNotes.ts) */
export interface SubmissionNote {
  id: string
  text: string
  target: 'video' | 'caption' | 'general'
  deliverableId: string | null
  timestampSec: number | null
  suggestion: { from: string; to: string } | null
  isResolved: boolean
  resolvedAt: string | null
  createdAt: string
  author: {
    id: string
    name: string | null
    avatar: string | null
    side: 'athlete' | 'brand'
  } | null
  replies: Omit<SubmissionNote, 'replies'>[]
}

export interface TimelineMessage {
  id: string
  messageType: 'user' | 'system' | 'review' | 'status_change' | 'submission'
  body: string
  metadata: Record<string, unknown> | null
  createdAt: string
  author: {
    id: string
    name: string
    avatar: string | null
  } | null
  milestone: {
    id: string
    name: string
    slug?: string
  } | null
}

export interface AthleteScouting {
  rateCard: import('@adthlete/common/utils/dealPricing').RateCard | null
  /** The recurring formats they offer, with how many of their posts fall in each category */
  themes: { id: string; label: string; category: string; categoryName: string; posts: number }[]
  /** Their top content categories across every post */
  contentMix: {
    category: string
    name: string
    posts: number
    share: number
    engagementRate: number
  }[]
  level: 'L1' | 'L2' | 'L3' | 'L4'
  score: number
  profileComplete: boolean
  completedDeals: number
  activeDeals: number
  athios: {
    posts: number
    totalReach: number
    engagementRate: number
    categories: {
      category: string
      name: string
      posts: number
      reach: number
      engagementRate: number
    }[]
  }
  highlights: {
    post_id: string
    link: string
    text: string
    image: string
    thumbnail: string
    type: 'photo' | 'video'
    platform: string
    stat: { likes: number; comments: number; plays: number; views: number }
  }[]
}

type Metric = { value: number; change: number | null; series: number[] }

export interface MarketerDashboard {
  days: number
  stats: {
    activeCampaigns: { value: number; brands: number }
    activeAthletes: { value: number }
    needsAction: { value: number }
    inProgress: { value: number }
    contentDelivered: { value: number; change: number | null }
  }
  attention: {
    id: string
    kind: 'application' | 'review' | 'brand_step'
    title: string
    detail: string
    action: string
    priority: 'high' | 'normal' | 'low'
    at: string
    href: string
    athlete: { name: string | null; avatar: string | null }
  }[]
  attentionTotal: number
  topAthletes: {
    id: string
    name: string | null
    avatar: string | null
    sport: string | null
    deals: number
    posts: number
    reach: number
    engagementRate: number
    change: number | null
    thumbnails: { url: string | null; link: string | null }[]
  }[]
  snapshot: {
    contentDelivered: Metric
    totalReach: Metric
    engagementRate: Metric
    spendToDate: Metric
  }
  content: {
    overallEngagementRate: number
    insights: {
      category: string
      name: string
      posts: number
      athletes: number
      reach: number
      avgReach: number
      engagementRate: number
      othersRate: number
      lift: number
      topPost: {
        thumbnail: string | null
        link: string | null
        caption: string | null
        athlete: string | null
      }
    }[]
  }
}

export interface InvoiceApplicationInput {
  legalName: string
  billingContactName: string
  billingEmail: string
  billingPhone?: string
  billingAddress: {
    line1: string
    line2?: string
    city: string
    state: string
    postalCode: string
    country?: string
  }
  annualSpend?: string
  termsRequestedDays: number
  poRequired: boolean
  taxId?: string
  notes?: string
}

export interface CatalogBrand {
  id: string
  name: string
  logo: string | null
  campaigns: number
  products: {
    id: string
    name: string
    category: string | null
    description: string | null
    imageUrl: string | null
    campaigns: number
  }[]
}

export interface SignInMethods {
  email: string
  hasPassword: boolean
  providers: Record<'google' | 'apple', { email: string | null; connectedAt: string } | null>
  available: Record<'google' | 'apple', boolean>
}

export interface NotificationPreferences {
  categories: { id: string; label: string; email: boolean; push: boolean }[]
}

export interface BillingInfo {
  method: 'card' | 'invoice'
  termsDays: number | null
  /** The approved spending limit with company credit */
  creditLimitCents: number | null
  /** A display summary of the saved card or bank account (the details live in Stripe) */
  paymentMethod:
    | {
        type: 'card'
        brand: string | null
        last4: string | null
        expMonth: number | null
        expYear: number | null
      }
    | { type: 'us_bank_account'; bankName: string | null; last4: string | null }
    | null
  activity: {
    pending: number
    paid: number
    items: {
      date: string
      type: 'Invoice' | 'Deal'
      reference: string | null
      campaign: string | null
      athlete: string | null
      amountCents: number
      status: 'paid' | 'due' | 'awaiting_funding'
      payUrl: string | null
    }[]
  }
  application: {
    status: 'pending' | 'approved' | 'declined'
    legalName: string
    termsRequestedDays: number
    submittedAt: string
    reviewedAt: string | null
    decisionNote: string | null
  } | null
}

/** An athlete's submission to an opportunity, or a pitch to one of the org's brands */
export interface Application {
  id: string
  campaignId: string | null
  title: string
  dealType: 'content' | 'gifting' | 'appearance' | 'licensing'
  dealTypeLabel: string
  source: 'opportunity' | 'pitch' | 'quote' | 'counter'
  /** What the brand booked on its offer, when the athlete's reply is a quote */
  booking:
    | { kind: 'custom'; description: string; askForRate: boolean }
    | {
        kind: 'rate_card'
        deliverableLabel: string
        quantity: number
        theme: { label: string } | null
      }
    | null
  ask: number | null
  statedPay: number | null
  message: string | null
  createdAt: string
  brand: { id: string; name: string; logo: string | null } | null
  athlete: {
    id: string
    name: string
    avatar: string | null
    bio: string | null
    school: string | null
    sports: string[]
    followers: number
    socials: { platform: string; username: string; followers: number }[]
  }
}

export interface DealPerformance {
  terms: {
    activationFee: number
    usageDays: number
    bonuses: { views: number; amount: number }[]
  } | null
  views: number
  posted: boolean
  bonus: {
    unlocked: { views: number; amount: number }[]
    total: number
    next: { views: number; amount: number } | null
    max: number
  } | null
  usage: {
    includedDays: number
    extensions: { termDays: number; price?: number; extendedAt: string }[]
    endsAt: string | null
    daysLeft: number | null
  }
}

export interface Collaboration {
  id: string
  /** This user archived the deal: it sits in the Archived tab, out of their working lists */
  archived?: boolean
  archivedAt?: string | null
  /** NIL Go clearance: required at $600+, filed within 5 business days of signing */
  compliance?: import('@adthlete/common/utils/nilCompliance').NilCompliance
  campaignId: string | null
  title?: string | null
  dealType?: 'content' | 'gifting' | 'appearance' | 'licensing'
  /** Gifting terms, bonuses unlocked by views, and the paid-usage window */
  performance?: DealPerformance | null
  standalone?: boolean
  athleteId: string
  status: string
  licensingEnabled: boolean
  paymentAmount: number | null
  paymentStatus: string
  complianceNeeded?: boolean
  nilGoStatus?: 'not_started' | 'in_progress' | 'confirmed' | 'not_required' | string
  nilGoConfirmationNumber?: string | null
  nilGoSubmittedAt?: string | null
  institutionalDisclosureStatus?:
    'not_started' | 'submitted' | 'acknowledged' | 'not_required' | string
  institutionalDisclosureSubmittedAt?: string | null
  contentDueDate: string | null
  postLinks: string[]
  offerDetails: {
    message?: string
    licensing_offer?: {
      offered_at: string
      message?: string | null
      total_amount: number
      platforms: Array<{
        platform: string
        usage_permission: string[]
        territory: string
        usage_term_days: number
        fee: number
      }>
    }
    /** Paid-usage extensions, each billed on its own invoice (`key` names it) */
    license_extensions?: Array<{
      key?: string
      termDays: number
      price?: number
      source?: string
      extendedAt: string
      extendedByUserId: number
    }>
  } | null
  createdAt: string
  updatedAt: string
  progress: number
  completedMilestones: number
  totalMilestones: number
  athlete: {
    id: string
    name: string
    email: string
    avatar: string | null
    gender?: string
    school?: string | null
    sport?: string | null
    sports?: string[]
    /** List view: followers across their accounts */
    followers?: number
  } | null
  campaign: {
    id: string
    title: string
    /** List view: the campaign's brand and the product it promotes */
    product?: string | null
    platform: string[]
    startDate: string
    endDate: string
    brief: string
    instructions: string | null
    brand: {
      id: number
      name: string
      logo: string | null
    } | null
  } | null
  milestones: Milestone[]
  timeline: TimelineMessage[]
  structuredDeliverables?: Array<{
    id: string
    platform: string
    format: string
    notes: string | null
    order: number
    dueDate?: string | null
    sameAsDeliverableId?: string | null
  }> | null
  invoice?: {
    id: string
    status: string
    invoiceNumber: string | null
    totalCents: number
    currency: string
    stripePaymentUrl: string | null
    sentToBrandAt: string | null
    paidAt: string | null
  } | null
  // Derived state fields
  actionOwner?: 'athlete' | 'marketer' | null
  currentMilestone?: { name: string; status: string } | null
  hasPendingReview?: boolean
  isComplete?: boolean
  // Whose turn it is on the active milestone (single source of truth from backend)
  turnState?: 'awaiting_review' | 'revising' | 'awaiting_submission' | 'done' | null
  /** List view: when the last step was finished */
  completedAt?: string | null
  /** List view: what the brand booked on the offer */
  booking?:
    | {
        kind: 'rate_card'
        deliverableLabel: string
        quantity: number
        theme: { label: string } | null
      }
    | { kind: 'custom'; description: string; askForRate: boolean }
    | null
  // Per-milestone summary for the segmented status bar
  milestoneBar?: Array<{
    slug: string
    name: string
    status: string
    complete: boolean
  }>
}

// Types from notificationsSlice
export interface Notification {
  id: number
  title: string
  body: string | null
  collaboration_id: string | null
  is_read: boolean
  created_at: string
  notification_type: string
  metadata?: {
    wave_id?: string
    collaboration_id?: string
    milestone_id?: string
    milestone_name?: string
    athlete_name?: string
    wave_title?: string
  }
}

// Types for todos
export interface TodoItem {
  id: string
  title: string
  completed: boolean
  priority?: string
  actionLink?: string
  type: string
}

// Pending brand/collaboration invitation addressed to the marketer
export interface MarketerInvitation {
  id: string
  token: string
  status: string
  email: string
  message: string | null
  createdAt: string
  expiresAt: string
  inviter: { name: string | null; avatar: string | null } | null
  collaboration: {
    title: string | null
    brandName: string | null
    paymentAmount: number | string | null
    offerDetails?: { deliverables?: string; message?: string } | null
    athleteName: string | null
  } | null
}

// Types for org invitations
export interface OrgInvite {
  id: string
  email: string
  role: string
  createdAt: string
  expiresAt: string
  status: string
}

export interface PublicOrgInvitation {
  org: { name: string; logo: string | null }
  inviter: { name: string | null; avatar: string | null }
  email: string
}

export interface DirectMessageThread {
  id: string
  subject: string | null
  lastMessageAt: string
  createdAt: string
  contact: {
    id: string
    name: string
    avatar: string | null
  }
  /** The talent sheet this conversation started from */
  talentSheet: { id: string; title: string } | null
  lastMessage: { body: string; isFromMe: boolean } | null
  unreadCount: number
}

export interface DirectMessage {
  id: string
  body: string
  createdAt: string
  isFromMe: boolean
  /** Who sent it, when that isn't you or the athlete, e.g. "Jane Doe · Manager" */
  byline?: string | null
  sender: {
    id: string
    name: string
    avatar: string | null
  }
}

export type FitPart = {
  key: 'audience' | 'content' | 'brand' | 'location'
  label: string
  score: number
  detail: string
}
export type CampaignFit = {
  campaignId: string | null
  campaignName: string | null
  score: number
  label: string
  parts: FitPart[]
  why: string
  reasons: string[]
}
export type MarketplaceAthlete = {
  id: string
  name: string
  avatar: string | null
  position: string | null
  school: string | null
  sports: string[]
  state: string | null
  followers: number
  engagementRate: number | null
  primary: { platform: string; followers: number } | null
  channels: {
    platform: string
    handle: string | null
    followers: number
    engagementRate: number | null
  }[]
  themes: { label: string; category: string }[]
  fromPrice: number | null
  completedDeals: number
  saved: boolean
  topMatch: boolean
  fit: CampaignFit
  /** Their content: theme examples first, then most-watched posts (up to three) */
  covers: {
    url: string
    thumbnail: string
    platform: string | null
    views: number | null
    theme: string | null
  }[]
}
export type MarketplaceResult = {
  target: {
    campaignId: string | null
    campaignName: string | null
    brandName: string | null
    sports: string[]
    states: string[]
    ages: string[]
    gender: string
    themes: string[]
    platforms: string[]
  }
  athletes: MarketplaceAthlete[]
  total: number
  hasMore: boolean
}
export type MarketplaceArgs = {
  campaignId?: string | null
  q?: string
  sport?: string
  audience?: string
  state?: string
  theme?: string
  view?: 'recommended' | 'all'
  limit?: number
}

/** What the intake understood (services/marketer/brandIntake.ts) */
export interface IntakeReading {
  summary: string
  campaignName: string
  dealTypes: ('licensing' | 'gifting' | 'appearance')[]
  dealType: 'licensing' | 'gifting' | 'appearance'
  customer: string | null
  ages: string[]
  gender: 'any' | 'female' | 'male'
  sports: number[]
  states: string[]
  keywords: string[]
  budgets: string[]
  mode: 'ai' | 'rules'
}

export const marketerApi = createApi({
  reducerPath: 'marketerApi',
  baseQuery,
  tagTypes: [
    'Campaign',
    'Shortlist',
    'SubmissionNotes',
    'BrandCatalog',
    'SignInMethods',
    'NotificationPreferences',
    'CampaignDetail',
    'Collaboration',
    'CollaborationDetail',
    'Notification',
    'DirectMessage',
    'CollaborationMessage',
    'AuctionListing',
    'AuctionBidMessages',
    'Invitation',
    'Application',
    'Brand',
    'Billing',
    'ToolInterest',
    'Dashboard',
  ],
  endpoints: (builder) => ({
    // Campaign Queries - Phase 2
    getCampaigns: builder.query<Campaign[], void>({
      query: () => '/marketer/campaigns',
      transformResponse: (response: { campaigns: Campaign[] }) => response.campaigns,
      providesTags: ['Campaign'],
    }),
    getCampaignDetails: builder.query<Campaign, string>({
      query: (campaignId) => `/marketer/campaigns/${campaignId}`,
      providesTags: (result, error, id) => [{ type: 'CampaignDetail', id }],
    }),

    // Collaboration Queries - Phase 3
    getCollaborations: builder.query<
      Collaboration[],
      { campaignId?: string; status?: string } | void
    >({
      query: (params) => ({
        url: '/marketer/collaborations',
        params: params || {},
      }),
      transformResponse: (response: { collaborations: Collaboration[] }) =>
        response.collaborations,
      providesTags: ['Collaboration'],
    }),
    getCollaboration: builder.query<Collaboration, string>({
      query: (id) => `/marketer/collaborations/${id}`,
      providesTags: (result, error, id) => [{ type: 'CollaborationDetail', id }],
    }),

    // Brand setup: brands, sports to pick from, the org's profile, the match preview, payments
    getBrands: builder.query<{ id: string; name: string; logo: string | null }[], void>({
      query: () => '/marketer/brands',
      transformResponse: (res: { data: { id: string; name: string; logo: string | null }[] }) =>
        res.data ?? [],
      providesTags: ['Brand'],
    }),
    getSports: builder.query<{ id: number; name: string }[], void>({
      query: () => '/public/sports',
    }),
    updateOrganization: builder.mutation<
      { organization: any },
      {
        name?: string
        description?: string
        logo?: string | null
        looking_for?: any
        gifting_bonuses?: { views: number; amount: number }[]
      }
    >({
      query: (body) => ({ url: '/marketer/organization', method: 'PATCH', body }),
    }),
    // The brand intake: first-screen answers read into setup and a first campaign
    interpretIntake: builder.mutation<
      IntakeReading,
      {
        goal: string
        customer: string
        ages: string[]
        gender: 'any' | 'female' | 'male'
        dealTypes: string[]
        budgets: string[]
        brandName: string | null
      }
    >({
      query: (body) => ({ url: '/marketer/intake/interpret', method: 'POST', body }),
    }),
    getMatchPreview: builder.query<
      {
        count: number
        inPlaces: number | null
        sample: {
          id: string
          name: string
          avatar: string | null
          school: string | null
          sport: string | null
        }[]
      },
      { sports: number[]; states: string[]; gender: string }
    >({
      query: (p) => ({
        url: '/marketer/organization/match-preview',
        params: { sports: p.sports.join(','), states: p.states.join(','), gender: p.gender },
      }),
    }),
    getStripeStatus: builder.query<
      { connected: boolean; status: string; onboarding_url?: string | null },
      void
    >({
      query: () => '/marketer/stripe/status',
    }),
    connectStripe: builder.mutation<{ url?: string; onboarding_url?: string }, void>({
      query: () => ({ url: '/marketer/stripe/connect', method: 'POST' }),
    }),

    // What a brand sees about an athlete beyond the public profile
    // An athlete's Marketplace page, with why they fit a campaign when one is given
    // An athlete's Content Shop from a shared link, for anyone (no sign-in needed)
    getPublicShop: builder.query<
      import('@adthlete/shared-frontend').MarketplaceProfileData,
      string
    >({ query: (id) => `/public/shop/${id}` }),
    getAthleteMarketplaceProfile: builder.query<
      import('@adthlete/shared-frontend').MarketplaceProfileData,
      { id: string; campaignId?: string | null }
    >({
      query: ({ id, campaignId }) => ({
        url: `/marketer/athletes/${id}/profile`,
        params: campaignId ? { campaignId } : {},
      }),
      providesTags: (r, e, { id }) => [{ type: 'Shortlist' as const, id }],
    }),
    setShortlisted: builder.mutation<{ shortlisted: boolean }, { id: string; on: boolean }>({
      query: ({ id, on }) => ({
        url: `/marketer/athletes/${id}/shortlist`,
        method: on ? 'PUT' : 'DELETE',
      }),
      invalidatesTags: (r, e, { id }) => [{ type: 'Shortlist' as const, id }],
    }),
    // The Marketplace: athletes ranked by fit for one of the brand's campaigns
    getMarketplace: builder.query<MarketplaceResult, MarketplaceArgs>({
      query: (args) => ({
        url: '/marketer/athletes/marketplace',
        params: Object.fromEntries(Object.entries(args).filter(([, v]) => v != null && v !== '')),
      }),
      providesTags: ['Shortlist'],
    }),
    getAthleteScouting: builder.query<AthleteScouting, string>({
      query: (id) => `/marketer/athletes/${id}/scouting`,
    }),

    // The home page: punch list, top athletes, performance snapshot, content insights
    getMarketerDashboard: builder.query<MarketerDashboard, number>({
      query: (days) => ({ url: '/marketer/dashboard', params: { days } }),
      providesTags: ['Dashboard'],
    }),

    // How the brand pays: pay as you go (Stripe) or invoiced on terms after approval
    // Settings > Brands: brands with their products
    getBrandCatalog: builder.query<{ brands: CatalogBrand[] }, void>({
      query: () => '/marketer/brands/catalog',
      providesTags: ['BrandCatalog'],
    }),
    saveProduct: builder.mutation<
      { brands: CatalogBrand[] },
      {
        brandId: string
        productId?: string
        name: string
        category?: string
        description?: string
        imageUrl?: string
      }
    >({
      query: ({ brandId, productId, ...body }) => ({
        url: productId
          ? `/marketer/brands/${brandId}/products/${productId}`
          : `/marketer/brands/${brandId}/products`,
        method: productId ? 'PATCH' : 'POST',
        body,
      }),
      invalidatesTags: ['BrandCatalog'],
    }),
    deleteProduct: builder.mutation<
      { brands: CatalogBrand[] },
      { brandId: string; productId: string }
    >({
      query: ({ brandId, productId }) => ({
        url: `/marketer/brands/${brandId}/products/${productId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['BrandCatalog'],
    }),
    // Settings > Account: how this person signs in
    getSignInMethods: builder.query<SignInMethods, void>({
      query: () => '/users/me/sign-in-methods',
      providesTags: ['SignInMethods'],
    }),
    getProviderLinkUrl: builder.mutation<{ url: string }, 'google' | 'apple'>({
      query: (provider) => ({
        url: `/users/oauth/${provider}/link-url`,
        method: 'POST',
        body: { origin: window.location.origin, base: appBaseUrl() },
      }),
    }),
    unlinkProvider: builder.mutation<SignInMethods, 'google' | 'apple'>({
      query: (provider) => ({ url: `/users/me/sign-in-methods/${provider}`, method: 'DELETE' }),
      invalidatesTags: ['SignInMethods'],
    }),
    // Settings > Notifications
    getNotificationPreferences: builder.query<NotificationPreferences, void>({
      query: () => '/users/me/notification-preferences',
      providesTags: ['NotificationPreferences'],
    }),
    setNotificationPreferences: builder.mutation<
      NotificationPreferences,
      Record<string, { email?: boolean; push?: boolean }>
    >({
      query: (preferences) => ({
        url: '/users/me/notification-preferences',
        method: 'PUT',
        body: { preferences },
      }),
      invalidatesTags: ['NotificationPreferences'],
    }),
    getBilling: builder.query<BillingInfo, void>({
      query: () => '/marketer/billing',
      providesTags: ['Billing'],
    }),
    // Tools we haven't built yet: which ones this brand would connect
    getToolInterest: builder.query<{ tools: string[] }, void>({
      query: () => '/marketer/tool-interest',
      providesTags: ['ToolInterest'],
    }),
    setToolInterest: builder.mutation<{ tools: string[] }, { tool: string; interested: boolean }>({
      query: ({ tool, interested }) => ({
        url: `/marketer/tool-interest/${tool}`,
        method: 'PUT',
        body: { interested },
      }),
      invalidatesTags: ['ToolInterest'],
    }),
    // Save a card or bank account through Stripe Checkout, then confirm it on return
    startPaymentMethodSetup: builder.mutation<{ url: string }, { kind: 'card' | 'bank' }>({
      query: ({ kind }) => ({
        url: '/marketer/billing/payment-method/setup',
        method: 'POST',
        body: { kind, origin: window.location.origin },
      }),
    }),
    confirmPaymentMethodSetup: builder.mutation<BillingInfo, string>({
      query: (sessionId) => ({
        url: '/marketer/billing/payment-method/confirm',
        method: 'POST',
        body: { sessionId },
      }),
      invalidatesTags: ['Billing'],
    }),
    applyForInvoicing: builder.mutation<BillingInfo, InvoiceApplicationInput>({
      query: (body) => ({ url: '/marketer/billing/invoice-application', method: 'POST', body }),
      invalidatesTags: ['Billing'],
    }),

    // Paid-usage licensing priced from the athlete's rate card
    getLicenseOptions: builder.query<
      { fromRateCard: boolean; options: { days: number; label: string; price: number }[] },
      string
    >({
      query: (collaborationId) => `/marketer/collaborations/${collaborationId}/license-options`,
    }),
    extendLicenseAtRateCard: builder.mutation<
      { days: number; price: number },
      { collaborationId: string; days: number }
    >({
      query: ({ collaborationId, days }) => ({
        url: `/marketer/collaborations/${collaborationId}/license-extension`,
        method: 'POST',
        body: { days },
      }),
      invalidatesTags: (_r, _e, { collaborationId }) => [
        { type: 'CollaborationDetail', id: collaborationId },
      ],
    }),

    // The brand's own deal steps, and inline review of the athlete's post / check-in / draft
    completeBrandStep: builder.mutation<
      { success: boolean; status: string },
      {
        collaborationId: string
        milestoneId: string
        metadata?: Record<string, string>
        note?: string
      }
    >({
      query: ({ collaborationId, milestoneId, ...body }) => ({
        url: `/marketer/collaborations/${collaborationId}/milestones/${milestoneId}/complete`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { collaborationId }) => [
        'Collaboration',
        'Campaign',
        'CampaignDetail',
        { type: 'CollaborationDetail', id: collaborationId },
      ],
    }),
    reviewSubmission: builder.mutation<
      any,
      {
        collaborationId: string
        milestoneId: string
        action: 'approve' | 'request_changes'
        /** Text, or for video reviews { deliverables: { [id]: { video, caption } } } */
        reviewNotes?: string | Record<string, unknown>
      }
    >({
      query: ({ collaborationId, milestoneId, action, reviewNotes }) => ({
        url: `/marketer/collaborations/${collaborationId}/milestones/${milestoneId}/review`,
        method: 'POST',
        body: { action, reviewNotes: reviewNotes ?? '' },
      }),
      invalidatesTags: (_r, _e, { collaborationId }) => [
        'Collaboration',
        'Campaign',
        'CampaignDetail',
        { type: 'CollaborationDetail', id: collaborationId },
      ],
    }),

    // Submissions: athletes applying to opportunities or pitching a brand
    getApplications: builder.query<Application[], void>({
      query: () => '/marketer/applications',
      transformResponse: (response: { applications: Application[] }) => response.applications,
      providesTags: ['Application'],
    }),
    respondToApplication: builder.mutation<
      { status: 'accepted' | 'offered' | 'rejected' },
      {
        id: string
        action: 'accept' | 'counter' | 'pass'
        message?: string
        paymentAmount?: number
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/marketer/applications/${id}/respond`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'Application',
        'Collaboration',
        'Campaign',
        'CampaignDetail',
        { type: 'CollaborationDetail', id },
      ],
    }),

    // Notification Queries - Phase 4
    getNotifications: builder.query<{ notifications: Notification[]; unreadCount: number }, void>({
      query: () => '/notifications',
      providesTags: ['Notification'],
    }),
    markNotificationRead: builder.mutation<void, number>({
      query: (id) => ({
        url: `/notifications/${id}/read`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Notification'],
    }),
    markAllNotificationsRead: builder.mutation<void, void>({
      query: () => ({
        url: '/notifications/mark-all-read',
        method: 'POST',
      }),
      invalidatesTags: ['Notification'],
    }),
    createCampaign: builder.mutation<any, any>({
      query: (data) => ({
        url: '/marketer/campaigns',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Campaign'],
    }),
    // Archive / unarchive a deal for this user only (moves it to the Archived tab)
    setCollaborationArchived: builder.mutation<
      { id: string; archived: boolean },
      { id: string; archived: boolean }
    >({
      query: ({ id, archived }) => ({
        url: `/marketer/collaborations/${id}/archive`,
        method: archived ? 'POST' : 'DELETE',
      }),
      invalidatesTags: ['Collaboration', 'Dashboard'],
    }),
    archiveCampaign: builder.mutation<any, string>({
      query: (campaignId) => ({
        url: `/marketer/campaigns/${campaignId}/archive`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => ['Campaign', { type: 'CampaignDetail', id }],
    }),
    deleteCampaign: builder.mutation<any, string>({
      query: (campaignId) => ({
        url: `/marketer/campaigns/${campaignId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => ['Campaign', { type: 'CampaignDetail', id }],
    }),
    createCollaboration: builder.mutation<any, any>({
      query: (data) => ({
        url: '/marketer/collaborations',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Collaboration'],
    }),
    negotiateCollaboration: builder.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => ({
        url: `/marketer/collaborations/${id}/negotiate`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Collaboration'],
    }),
    updateOfferTerms: builder.mutation<
      any,
      {
        id: string
        data: {
          paymentType?: 'fixed' | 'custom'
          paymentAmount?: number
          customPaymentTerms?: string
          contentDueDate?: string
          platform?: string[]
        }
      }
    >({
      query: ({ id, data }) => ({
        url: `/marketer/collaborations/${id}/offer-terms`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        'Collaboration',
        { type: 'CollaborationDetail', id },
      ],
    }),
    // Take back an offer the athlete hasn't answered; it stays on the deal list as withdrawn
    withdrawOffer: builder.mutation<{ status: 'withdrawn' }, { id: string; message?: string }>({
      query: ({ id, message }) => ({
        url: `/marketer/collaborations/${id}/withdraw`,
        method: 'POST',
        body: { message },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'Collaboration',
        'Campaign',
        'CampaignDetail',
        'Dashboard',
        { type: 'CollaborationDetail', id },
      ],
    }),
    updateMilestoneDueDate: builder.mutation<
      any,
      {
        collaborationId: string
        milestoneId: string
        dueDate: string | null
      }
    >({
      query: ({ collaborationId, milestoneId, dueDate }) => ({
        url: `/marketer/collaborations/${collaborationId}/milestones/${milestoneId}/due-date`,
        method: 'PATCH',
        body: { dueDate },
      }),
      invalidatesTags: (result, error, { collaborationId }) => [
        'Collaboration',
        { type: 'CollaborationDetail', id: collaborationId },
      ],
    }),
    respondToMilestoneDateProposal: builder.mutation<
      any,
      {
        collaborationId: string
        milestoneId: string
        proposalId: string
        accept: boolean
        responseNote?: string | null
      }
    >({
      query: ({ collaborationId, milestoneId, proposalId, accept, responseNote }) => ({
        url: `/marketer/collaborations/${collaborationId}/milestones/${milestoneId}/date-proposals/${proposalId}/respond`,
        method: 'POST',
        body: { accept, responseNote: responseNote ?? null },
      }),
      invalidatesTags: (_result, _error, { collaborationId }) => [
        'Collaboration',
        { type: 'CollaborationDetail', id: collaborationId },
      ],
    }),
    getMyInvitations: builder.query<{ invitations: MarketerInvitation[] }, void>({
      query: () => '/marketer/invitations',
      providesTags: ['Invitation'],
    }),
    getInvitation: builder.query<any, string>({
      query: (token) => `/marketer/invitations/${token}`,
    }),
    acceptInvitation: builder.mutation<
      any,
      { token: string; waveTitle: string; brandId?: string }
    >({
      query: ({ token, waveTitle, brandId }) => ({
        url: `/marketer/invitations/${token}/accept`,
        method: 'POST',
        body: { waveTitle, brandId },
      }),
      invalidatesTags: ['Campaign', 'Collaboration', 'Invitation'],
    }),
    declineInvitation: builder.mutation<void, string>({
      query: (token) => ({
        url: `/marketer/invitations/${token}/decline`,
        method: 'POST',
      }),
      invalidatesTags: ['Invitation'],
    }),
    // Todos
    getMarketerTodos: builder.query<{ todos: TodoItem[] }, void>({
      query: () => '/marketer/todos',
    }),

    // Org invitation endpoints
    getPublicOrgInvitation: builder.query<PublicOrgInvitation, string>({
      query: (token) => `/users/org-invite/${token}`,
    }),
    acceptOrgInvitation: builder.mutation<{ message: string; org_id: string }, string>({
      query: (token) => ({
        url: `/marketer/organization/invitations/${token}/accept`,
        method: 'POST',
      }),
    }),
    revokeOrgInvitation: builder.mutation<{ success: boolean }, string>({
      query: (invitationId) => ({
        url: `/marketer/organization/invitations/${invitationId}`,
        method: 'DELETE',
      }),
    }),

    // Direct Messages
    createDirectMessageThread: builder.mutation<
      { thread: DirectMessageThread; message: DirectMessage },
      { athleteId: string; message: string; talentSheetId?: string }
    >({
      query: (data) => ({
        url: '/messages',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['DirectMessage'],
    }),
    getDirectMessageThreads: builder.query<{ threads: DirectMessageThread[] }, void>({
      query: () => '/messages',
      providesTags: ['DirectMessage'],
    }),
    getDirectThreadMessages: builder.query<
      {
        thread: {
          id: string
          subject: string | null
          contact: { id: string; name: string; avatar: string | null } | null
          org: { id: number; name: string; logo: string | null } | null
          talentSheet: { id: string; title: string } | null
          /** Deals offered from this conversation, newest first */
          deals: ThreadDeal[]
        }
        messages: DirectMessage[]
      },
      string
    >({
      query: (threadId) => `/messages/${threadId}/messages`,
      providesTags: (result, error, threadId) => [{ type: 'DirectMessage', id: threadId }],
    }),
    // Review notes on a submission: timestamped video notes, caption edits, comments
    getSubmissionNotes: builder.query<
      SubmissionNote[],
      { collaborationId: string; submissionId: string }
    >({
      query: ({ collaborationId, submissionId }) =>
        `/marketer/collaborations/${collaborationId}/submissions/${submissionId}/notes`,
      transformResponse: (r: { notes: SubmissionNote[] }) => r.notes,
      providesTags: (r, e, { submissionId }) => [
        { type: 'SubmissionNotes' as const, id: submissionId },
      ],
    }),
    addSubmissionNote: builder.mutation<
      SubmissionNote[],
      {
        collaborationId: string
        submissionId: string
        text: string
        metadata:
          | { target: 'video'; deliverableId: string | null; timestampSec: number }
          | {
              target: 'caption'
              deliverableId: string | null
              suggestion?: { from: string; to: string }
            }
          | { target: 'general' }
        parentId?: string
      }
    >({
      query: ({ collaborationId, submissionId, ...body }) => ({
        url: `/marketer/collaborations/${collaborationId}/submissions/${submissionId}/notes`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (r, e, { submissionId }) => [
        { type: 'SubmissionNotes' as const, id: submissionId },
      ],
    }),
    resolveSubmissionNote: builder.mutation<
      SubmissionNote[],
      { collaborationId: string; submissionId: string; noteId: string; resolved: boolean }
    >({
      query: ({ collaborationId, noteId, resolved }) => ({
        url: `/marketer/collaborations/${collaborationId}/notes/${noteId}`,
        method: 'PATCH',
        body: { resolved },
      }),
      invalidatesTags: (r, e, { submissionId }) => [
        { type: 'SubmissionNotes' as const, id: submissionId },
      ],
    }),
    // The conversation inside one collaboration
    getCollaborationMessages: builder.query<TimelineMessage[], string>({
      query: (collaborationId) => `/marketer/collaborations/${collaborationId}/messages`,
      transformResponse: (r: { messages: TimelineMessage[] }) => r.messages,
      providesTags: (r, e, id) => [{ type: 'CollaborationDetail' as const, id }],
    }),
    sendCollaborationMessage: builder.mutation<void, { collaborationId: string; message: string }>(
      {
        query: ({ collaborationId, message }) => ({
          url: `/marketer/collaborations/${collaborationId}/messages`,
          method: 'POST',
          body: { message },
        }),
        invalidatesTags: (result, error, { collaborationId }) => [
          { type: 'CollaborationDetail', id: collaborationId },
        ],
      },
    ),
    sendDirectMessageReply: builder.mutation<DirectMessage, { threadId: string; message: string }>(
      {
        query: ({ threadId, message }) => ({
          url: `/messages/${threadId}/messages`,
          method: 'POST',
          body: { message },
        }),
        invalidatesTags: (result, error, { threadId }) => [
          'DirectMessage',
          { type: 'DirectMessage', id: threadId },
        ],
      },
    ),
  }),
})

export const {
  useGetLicenseOptionsQuery,
  useExtendLicenseAtRateCardMutation,
  useGetAthleteScoutingQuery,
  useGetAthleteMarketplaceProfileQuery,
  useGetPublicShopQuery,
  useSetShortlistedMutation,
  useGetMarketplaceQuery,
  useGetMarketerDashboardQuery,
  useGetToolInterestQuery,
  useSetToolInterestMutation,
  useGetBillingQuery,
  useApplyForInvoicingMutation,
  useStartPaymentMethodSetupMutation,
  useGetBrandCatalogQuery,
  useSaveProductMutation,
  useDeleteProductMutation,
  useGetSignInMethodsQuery,
  useGetProviderLinkUrlMutation,
  useUnlinkProviderMutation,
  useGetNotificationPreferencesQuery,
  useSetNotificationPreferencesMutation,
  useConfirmPaymentMethodSetupMutation,
  useGetBrandsQuery,
  useGetSportsQuery,
  useUpdateOrganizationMutation,
  useGetMatchPreviewQuery,
  useInterpretIntakeMutation,
  useGetStripeStatusQuery,
  useConnectStripeMutation,
  useCompleteBrandStepMutation,
  useReviewSubmissionMutation,
  useGetApplicationsQuery,
  useRespondToApplicationMutation,
  useGetCampaignsQuery,
  useGetCampaignDetailsQuery,
  useGetCollaborationsQuery,
  useGetCollaborationQuery,
  useGetNotificationsQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  useCreateCampaignMutation,
  useArchiveCampaignMutation,
  useSetCollaborationArchivedMutation,
  useDeleteCampaignMutation,
  useCreateCollaborationMutation,
  useNegotiateCollaborationMutation,
  useUpdateOfferTermsMutation,
  useWithdrawOfferMutation,
  useUpdateMilestoneDueDateMutation,
  useRespondToMilestoneDateProposalMutation,
  useGetMyInvitationsQuery,
  useGetInvitationQuery,
  useAcceptInvitationMutation,
  useDeclineInvitationMutation,
  useGetMarketerTodosQuery,
  useGetPublicOrgInvitationQuery,
  useAcceptOrgInvitationMutation,
  useRevokeOrgInvitationMutation,
  useCreateDirectMessageThreadMutation,
  useGetDirectMessageThreadsQuery,
  useGetDirectThreadMessagesQuery,
  useSendDirectMessageReplyMutation,
  useSendCollaborationMessageMutation,
  useGetSubmissionNotesQuery,
  useAddSubmissionNoteMutation,
  useResolveSubmissionNoteMutation,
  useGetCollaborationMessagesQuery,
} = marketerApi
